import { useEffect, useRef, useState } from "react";
import { sanitizeNonNegativeInput } from "../../utils/nonNegativeInput";
import { DEFAULT_PLATFORMS } from "../../services/platformCatalog";
import {
  adjustedPlatformTotal,
  applyAccountBalance,
  applyAccountDeposit,
  applyAccountWithdrawal,
  getBankrollBaseValue,
  parseMoney,
  simpleChartThemes,
  withTodaySnapshot,
} from "../../services/simplifiedBankroll";
import DeleteAccountModal from "./DeleteAccountModal";
import PlatformAccountGrid from "./PlatformAccountGrid";
import SimplifiedBankrollChart from "./SimplifiedBankrollChart";
import SimplifiedControls from "./SimplifiedControls";
import SimplifiedSummary from "./SimplifiedSummary";
import {
  compareAccountsWithPrimarySuprema,
  comparePlatformNames,
  createPlatformAccount,
  getSupremaAliasOrFallback,
  getSupremaDisplayAlias,
  getUniqueSupremaAlias,
  platformHasFinancialValue,
  SUPREMA_ACCOUNT_LIMIT,
  SUPREMA_PLATFORM,
} from "./simplifiedAccounts";
import { useSimplifiedChart } from "./useSimplifiedChart";

export default function SimplifiedMode({
  bankroll,
  onChange,
  theme,
  displayCurrency,
}) {
  const [platformPickerOpen, setPlatformPickerOpen] = useState(false);
  const [withdrawalDrafts, setWithdrawalDrafts] = useState({});
  const [depositDrafts, setDepositDrafts] = useState({});
  const [supremaAlias, setSupremaAlias] = useState("");
  const [editingSupremaAccountId, setEditingSupremaAccountId] = useState(null);
  const [deleteAccountTarget, setDeleteAccountTarget] = useState(null);
  const supremaAliasInputRef = useRef(null);

  const accounts = Array.isArray(bankroll.accounts) ? bankroll.accounts : [];
  const primarySupremaAccountId =
    accounts.find((account) => account.name === SUPREMA_PLATFORM)?.id || null;
  const selectedAccounts = [...accounts].sort(
    compareAccountsWithPrimarySuprema(primarySupremaAccountId, accounts)
  );
  const selectedPlatformNames = new Set(accounts.map((account) => account.name));
  const hasAccountValues = selectedAccounts.some(
    (account) => String(account.balance || "").trim() !== ""
  );

  const currencyOptions = { displayCurrency };
  const bancaAtual = adjustedPlatformTotal(bankroll, currencyOptions);
  const bancaBase = getBankrollBaseValue(bankroll, currencyOptions);
  const lucro = hasAccountValues ? bancaAtual - bancaBase : 0;
  const chartColors = simpleChartThemes[theme] || simpleChartThemes.dark;
  const {
    chartData,
    chartFilters,
    clearChartFilters,
    hasActiveFilters,
    hasSourceHistory,
    lucroDia,
    platformOptions: chartPlatformOptions,
    pointLabels,
    setChartFilter,
    sortedHistory,
  } = useSimplifiedChart({
    accounts,
    bankroll,
    chartColors,
    displayCurrency,
    hasAccountValues,
  });
  const supremaAccountCount = accounts.filter(
    (account) => account.name === SUPREMA_PLATFORM
  ).length;
  const canAddSupremaAccount = supremaAccountCount < SUPREMA_ACCOUNT_LIMIT;
  const platformCountLabel = `${selectedAccounts.length || 0} banca${
    selectedAccounts.length === 1 ? "" : "s"
  }`;
  const platformOptions = [...DEFAULT_PLATFORMS].sort(comparePlatformNames);
  const resultTone = lucro >= 0 ? "positive" : "negative";

  useEffect(() => {
    if (!editingSupremaAccountId) return;
    supremaAliasInputRef.current?.focus();
    supremaAliasInputRef.current?.select();
  }, [editingSupremaAccountId]);

  const setBase = (value) => {
    onChange((current) =>
      withTodaySnapshot({
        ...current,
        base: sanitizeNonNegativeInput(value, 12),
      })
    );
  };

  const setAccountBalance = (accountId, value) => {
    onChange((current) =>
      applyAccountBalance(current, accountId, sanitizeNonNegativeInput(value, 12))
    );
  };

  const setWithdrawalDraft = (accountId, value) => {
    setWithdrawalDrafts((current) => ({
      ...current,
      [accountId]: sanitizeNonNegativeInput(value, 12),
    }));
  };

  const setDepositDraft = (accountId, value) => {
    setDepositDrafts((current) => ({
      ...current,
      [accountId]: sanitizeNonNegativeInput(value, 12),
    }));
  };

  const confirmWithdrawal = (accountId) => {
    const value = withdrawalDrafts[accountId];
    if (parseMoney(value) <= 0) return;

    onChange((current) => applyAccountWithdrawal(current, accountId, value));
    setWithdrawalDrafts((current) => ({ ...current, [accountId]: "" }));
  };

  const confirmDeposit = (accountId) => {
    const value = depositDrafts[accountId];
    if (parseMoney(value) <= 0) return;

    onChange((current) => applyAccountDeposit(current, accountId, value));
    setDepositDrafts((current) => ({ ...current, [accountId]: "" }));
  };

  const removePlatform = (platform) => {
    onChange((current) => {
      const currentAccounts = Array.isArray(current.accounts) ? current.accounts : [];

      return withTodaySnapshot({
        ...current,
        accounts: currentAccounts.filter((account) => account.name !== platform),
      });
    });
  };

  const addPlatform = (platform) => {
    onChange((current) => {
      const currentAccounts = Array.isArray(current.accounts) ? current.accounts : [];

      return withTodaySnapshot({
        ...current,
        accounts: [
          ...currentAccounts,
          createPlatformAccount(
            platform,
            platform === SUPREMA_PLATFORM
              ? getSupremaAliasOrFallback(currentAccounts, "")
              : ""
          ),
        ],
      });
    });
  };

  const togglePlatform = (platform) => {
    const hasPlatform = accounts.some((account) => account.name === platform);

    if (!hasPlatform) {
      addPlatform(platform);
      return;
    }

    if (platformHasFinancialValue(accounts, platform)) {
      setDeleteAccountTarget({ type: "platform", platform });
      return;
    }

    removePlatform(platform);
  };

  const commitSupremaAlias = (accountId, aliasDraft = supremaAlias) => {
    const alias = getUniqueSupremaAlias(accounts, aliasDraft, accountId);

    onChange((current) => {
      const currentAccounts = Array.isArray(current.accounts) ? current.accounts : [];
      const nextAlias = getUniqueSupremaAlias(currentAccounts, alias, accountId);

      return withTodaySnapshot({
        ...current,
        accounts: currentAccounts.map((account) =>
          account.id === accountId ? { ...account, alias: nextAlias } : account
        ),
      });
    });
    setSupremaAlias("");
    setEditingSupremaAccountId(null);
  };

  const addSupremaAccount = () => {
    if (!canAddSupremaAccount) return;

    const alias = getSupremaAliasOrFallback(accounts, "");
    const account = createPlatformAccount(SUPREMA_PLATFORM, alias);

    onChange((current) => {
      const currentAccounts = Array.isArray(current.accounts) ? current.accounts : [];
      const currentSupremaAccounts = currentAccounts.filter(
        (currentAccount) => currentAccount.name === SUPREMA_PLATFORM
      );

      if (currentSupremaAccounts.length >= SUPREMA_ACCOUNT_LIMIT) return current;

      return withTodaySnapshot({
        ...current,
        accounts: [...currentAccounts, account],
      });
    });

    setSupremaAlias(alias);
    setEditingSupremaAccountId(account.id);
  };

  const removeAccount = (accountId) => {
    onChange((current) =>
      withTodaySnapshot({
        ...current,
        accounts: (current.accounts || []).filter((account) => account.id !== accountId),
      })
    );
    setDeleteAccountTarget(null);
  };

  const startEditingSupremaAlias = (account) => {
    setSupremaAlias(getSupremaDisplayAlias(account));
    setEditingSupremaAccountId(account.id);
  };

  const removeDeleteTarget = (target) => {
    if (target.type === "platform") {
      removePlatform(target.platform);
      setDeleteAccountTarget(null);
      return;
    }

    removeAccount(target.id);
  };

  return (
    <div className="simple-mode">
      <SimplifiedSummary
        bancaBase={bancaBase}
        lucro={lucro}
        lucroDia={lucroDia}
        platformCountLabel={platformCountLabel}
        resultTone={resultTone}
        displayCurrency={displayCurrency}
      />

      <SimplifiedControls
        bankroll={bankroll}
        onBaseChange={setBase}
        platformOptions={platformOptions}
        platformPickerOpen={platformPickerOpen}
        selectedPlatformNames={selectedPlatformNames}
        setPlatformPickerOpen={setPlatformPickerOpen}
        togglePlatform={togglePlatform}
      />

      <PlatformAccountGrid
        addSupremaAccount={addSupremaAccount}
        canAddSupremaAccount={canAddSupremaAccount}
        commitSupremaAlias={commitSupremaAlias}
        confirmDeposit={confirmDeposit}
        confirmWithdrawal={confirmWithdrawal}
        depositDrafts={depositDrafts}
        editingSupremaAccountId={editingSupremaAccountId}
        primarySupremaAccountId={primarySupremaAccountId}
        selectedAccounts={selectedAccounts}
        setAccountBalance={setAccountBalance}
        setDeleteAccountTarget={setDeleteAccountTarget}
        setDepositDraft={setDepositDraft}
        setSupremaAlias={setSupremaAlias}
        setWithdrawalDraft={setWithdrawalDraft}
        startEditingSupremaAlias={startEditingSupremaAlias}
        supremaAlias={supremaAlias}
        supremaAliasInputRef={supremaAliasInputRef}
        withdrawalDrafts={withdrawalDrafts}
      />

      <DeleteAccountModal
        deleteAccountTarget={deleteAccountTarget}
        onCancel={() => setDeleteAccountTarget(null)}
        onRemove={removeDeleteTarget}
        theme={theme}
      />

      <SimplifiedBankrollChart
        chartColors={chartColors}
        chartData={chartData}
        chartFilters={chartFilters}
        clearChartFilters={clearChartFilters}
        hasActiveFilters={hasActiveFilters}
        hasSourceHistory={hasSourceHistory}
        platformOptions={chartPlatformOptions}
        pointLabels={pointLabels}
        setChartFilter={setChartFilter}
        sortedHistory={sortedHistory}
        theme={theme}
        displayCurrency={displayCurrency}
      />
    </div>
  );
}
